import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, lastValueFrom } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { ProbateSummary } from '../Models/ProbateSummary';
import { User } from '../Models/User';
import moment, { Moment } from 'moment';
import { UserService } from '../services/user.service';
import { PROBATE_LIST_FILTERS_KEY, ProbateListFilterStorageService } from '../services/probate-list-filter-storage.service';

// Which of the four ways of viewing the list is currently in effect. Exactly one at a time,
// set explicitly by whichever action the user takes (applyFilter/applyNameSearch/
// applyProbateSearch/applyDateSearch) - see populateComponents() for why this replaced three
// independent "isXSearchActive" flags: those could go stale (e.g. a search flag left set after
// Apply Filter was clicked) and silently keep overriding the filter on every reload.
type ProbateListView = 'filter' | 'nameSearch' | 'probateSearch' | 'dateSearch';

interface StoredProbateListFilters {
  selectedStatusFilters: number[];
  selectedUserFilter: number | 'unassigned' | null;
  showNonCharitable: boolean;
  nameSearchValue: string;
  probateSearchValue: string;
  startDate: string | null;
  endDate: string | null;
  activeView: ProbateListView;
}

@Component({
  selector: 'app-probate-list',
  templateUrl: './probate-list.component.html',
  styleUrls: ['./probate-list.component.css'],
  standalone: false
})
export class ProbateListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({ required: true }) loggedInUser?: User = undefined;

  private http = inject(HttpClient);
  route = inject(ActivatedRoute);
  router = inject(Router);

  displayedColumns: string[] = [
    'select',
    'caseReferenceNumber',
    'fullNameOfDeceased',
    'clientCharitableBequestCount',
    'nonClientCharitableBequestCount',
    'charitableBequestCount',
    'nonCharitableBequestCount',
    'dateOfIssue',
    'weekOld',
    'assignedUser',
    'hasNotes',
    'status'
  ];

  dataSource = new MatTableDataSource<ProbateSummary>([]);
  selection = new SelectionModel<ProbateSummary>(true, []);
  // Mirrors what MatTable actually paints (after filter → sort → paginate) so the
  // master checkbox and "is all selected?" checks always operate on the visible rows.
  private renderedRows: ProbateSummary[] = [];
  users: User[] = [];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  selectedUserFilter: User | 'unassigned' | null = null;
  assignToUserControl = new FormControl<User | 'unassign' | null>(null);

  // Status filter options matching EstateWorkflowStatus enum (numeric values)
  statusOptions = [
    { value: 0, label: 'Unreadable' },
    { value: 1, label: 'Processing' },
    { value: 2, label: 'Reported' },
    { value: 3, label: 'Verified' },
    { value: 4, label: 'Completed' },
    { value: 5, label: 'On Hold' }
  ];
  // Default: Processing (1), Reported (2)
  selectedStatusFilters: number[] = [1, 2];

  ProbateSummaries: ProbateSummary[] = [];

  private loggedInUserTag = "";
  private loggedInUser2?: User = undefined;
  private hasInitialisedUserFilter = false;
  // A saved user-filter id waiting to be matched against `users` once it loads.
  // `undefined` = nothing to restore; `null` / `'unassigned'` are valid restored values.
  private pendingUserFilterId: number | 'unassigned' | null | undefined = undefined;

  // ✅ NEW: Page size persistence
  pageSize = 50;

  // Show Non-Charitable filter (default unchecked = hide non-charitable)
  showNonCharitable = false;

  // Date filter
  startDate: Moment | null = null;
  endDate: Moment | null = null;

  // See ProbateListView above - the single source of truth for filter-vs-search precedence.
  activeView: ProbateListView = 'filter';
  get isNameSearchActive(): boolean { return this.activeView === 'nameSearch'; }
  get isProbateSearchActive(): boolean { return this.activeView === 'probateSearch'; }
  get isDateSearchActive(): boolean { return this.activeView === 'dateSearch'; }

  constructor(
    private toastr: ToastrService,
    private userService: UserService,
    private filterStorage: ProbateListFilterStorageService
  ) { }

  ngOnInit(): void {
    // Load saved page size
    const saved = localStorage.getItem('probate-page-size');
    if (saved) {
      this.pageSize = +saved;
    }

    // A reset was explicitly requested (logout/login, or the "Probate List" nav button - even if
    // pressed while already on this screen) - skip restoring anything and start from the plain
    // defaults this component was constructed with. Must win even if an outgoing instance of
    // this same component just re-saved its old filters a moment ago - see
    // ProbateListFilterStorageService.requestReset for why that race exists.
    if (this.filterStorage.consumeResetRequest()) {
      localStorage.removeItem(PROBATE_LIST_FILTERS_KEY);
    } else {
      this.restoreFiltersFromStorage();
    }

    this.extractRouteValues();
    this.populateComponents();

    this.dataSource.filterPredicate = (data: ProbateSummary, filter: string) => {
      const searchStr = (
        data.probateId + ' ' +
        data.deceasedName + ' ' +
        data.subscribedCharitableBequestCount + ' ' +
        data.nonSubscribedCharitableBequestCount + ' ' +
        data.nonCharitableBequestCount// + ' ' +
        // (data.assignedUser ? data.assignedUser.firstName + ' ' + data.assignedUser.lastName : '')
      ).toLowerCase();
      return searchStr.includes(filter);
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item: ProbateSummary, property: string) => {
      switch (property) {
        case 'fullNameOfDeceased': return item.deceasedName?.toLowerCase() ?? '';
        case 'assignedUser': return item.assignedUser ? `${item.assignedUser.firstName} ${item.assignedUser.lastName}`.toLowerCase() : '';
        // Week Old: older issue date = higher week count. Sort on the date itself (ascending date = lower week count).
        case 'weekOld': return item.dateOfIssue ? new Date(item.dateOfIssue).getTime() : Number.MAX_SAFE_INTEGER;
        case 'reportedBy': return item.reportedByUser ? `${item.reportedByUser.firstName} ${item.reportedByUser.lastName}`.toLowerCase() : '';
        case 'verifiedBy': return item.verifiedByUser ? `${item.verifiedByUser.firstName} ${item.verifiedByUser.lastName}`.toLowerCase() : '';
        // Has Notes column displays Y/N based on noteCount > 0. Sort with 1/0 so the visual
        // grouping (all N rows together, all Y rows together) matches the displayed value.
        case 'hasNotes': return item.noteCount > 0 ? 1 : 0;
        default: return (item as any)[property];
      }
    };

    // Apply paginator and restore saved page size
    this.paginator.pageSize = this.pageSize;
    this.dataSource.paginator = this.paginator;

    // Track the rendered rows (post filter/sort/paginate) for the master checkbox / "is all
    // selected?" checks. Notes themselves now arrive embedded on each row from the initial list
    // load (see ProbateSummary.notes), so there's no separate per-row fetch here any more.
    this.dataSource.connect().subscribe(rows => {
      this.renderedRows = rows;
    });

    // A new sort order is a new selection intent — drop the prior selection so the
    // user starts fresh on the re-sorted view. Without this, a follow-up "Select all"
    // would silently combine the pre-sort selection with the new visible rows.
    this.sort.sortChange.subscribe(() => this.selection.clear());
  }

  // ✅ NEW: Page change handler
  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    localStorage.setItem('probate-page-size', String(event.pageSize));
  }

  ngOnDestroy(): void {
    // The app's RouteReuseStrategy destroys this component on every navigation away
    // (including browser back), so this is the one reliable place to persist the
    // filters the user had selected for when they return to the list.
    this.saveFiltersToStorage();
  }

  private restoreFiltersFromStorage(): void {
    const saved = localStorage.getItem(PROBATE_LIST_FILTERS_KEY);
    if (!saved) return;

    try {
      const parsed: StoredProbateListFilters = JSON.parse(saved);
      this.selectedStatusFilters = parsed.selectedStatusFilters ?? this.selectedStatusFilters;
      this.showNonCharitable = parsed.showNonCharitable ?? this.showNonCharitable;
      this.nameSearchValue = parsed.nameSearchValue ?? '';
      this.probateSearchValue = parsed.probateSearchValue ?? '';
      this.startDate = parsed.startDate ? moment(parsed.startDate) : null;
      this.endDate = parsed.endDate ? moment(parsed.endDate) : null;
      this.activeView = parsed.activeView ?? 'filter';
      this.pendingUserFilterId = parsed.selectedUserFilter ?? null;
      // A restored filter set (even an explicit "All users") takes precedence over the
      // Supervisor auto-select-self default in populateComponents().
      this.hasInitialisedUserFilter = true;
    } catch {
      // Corrupt/old-shape data - ignore and fall back to defaults.
    }
  }

  private saveFiltersToStorage(): void {
    const toStore: StoredProbateListFilters = {
      selectedStatusFilters: this.selectedStatusFilters,
      selectedUserFilter: this.selectedUserFilter === 'unassigned'
        ? 'unassigned'
        : (this.selectedUserFilter?.id ?? null),
      showNonCharitable: this.showNonCharitable,
      nameSearchValue: this.nameSearchValue,
      probateSearchValue: this.probateSearchValue,
      startDate: this.startDate ? this.startDate.toISOString() : null,
      endDate: this.endDate ? this.endDate.toISOString() : null,
      activeView: this.activeView
    };
    localStorage.setItem(PROBATE_LIST_FILTERS_KEY, JSON.stringify(toStore));
  }

  nameSearchValue: string = '';

  applyNameSearch() {
    const name = this.nameSearchValue.trim();
    if (!name) return;
    const params = new HttpParams().set('name', name).set('user', this.loggedInUserTag);
    this.http.get<ProbateSummary[]>('/charitycentral/getprobatesummariesbyname', { params })
      .subscribe({
        next: (results) => {
          this.selection.clear();
          this.dataSource.data = results;
          this.activeView = 'nameSearch';
        },
        error: (err) => {
          this.toastr.error(`Search error (${err.status})`, '', { timeOut: 5000 });
        }
      });
  }

  clearNameSearch() {
    this.nameSearchValue = '';
    this.activeView = 'filter';
    this.applyAllFilters();
  }

  probateSearchValue: string = '';

  applyProbateSearch() {
    const id = this.probateSearchValue.trim();
    if (!id) return;
    const params = new HttpParams().set('probateId', id).set('user', this.loggedInUserTag);
    this.http.get<ProbateSummary>('/charitycentral/getprobatesummarybyid', { params })
      .subscribe({
        next: (result) => {
          this.selection.clear();
          this.dataSource.data = [result];
          this.activeView = 'probateSearch';
        },
        error: () => {
          this.selection.clear();
          this.dataSource.data = [];
          this.activeView = 'probateSearch';
        }
      });
  }

  clearProbateSearch() {
    this.probateSearchValue = '';
    this.activeView = 'filter';
    this.applyAllFilters();
  }

  applyDateSearch() {
    if (!this.startDate && !this.endDate) return;
    let params = new HttpParams().set('user', this.loggedInUserTag);
    if (this.startDate) params = params.set('dateFrom', this.startDate.format('YYYY-MM-DD'));
    if (this.endDate) params = params.set('dateTo', this.endDate.format('YYYY-MM-DD'));
    this.http.get<ProbateSummary[]>('/charitycentral/getprobatesummariesbydate', { params })
      .subscribe({
        next: (results) => {
          this.selection.clear();
          this.dataSource.data = results;
          this.activeView = 'dateSearch';
        },
        error: (err) => {
          this.toastr.error(`Search error (${err.status})`, '', { timeOut: 5000 });
        }
      });
  }

  clearDateSearch() {
    this.startDate = null;
    this.endDate = null;
    this.activeView = 'filter';
    this.applyAllFilters();
  }

  applySearch() {
    if (this.probateSearchValue.trim()) {
      this.applyProbateSearch();
    } else if (this.nameSearchValue.trim()) {
      this.applyNameSearch();
    } else if (this.startDate || this.endDate) {
      this.applyDateSearch();
    }
  }

  applyQuickFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  // "Apply Filter" button: explicitly switches back to the filter view (status/user/
  // non-charitable), overriding whatever search - if any - was previously active, then
  // reloads from the server with the current filters. Without forcing activeView here,
  // a search left active from earlier would silently keep winning inside populateComponents()
  // instead of the filter the user just asked for.
  applyFilter() {
    this.activeView = 'filter';
    this.populateComponents();
  }

  applyAllFilters() {
    let filteredData = [...this.ProbateSummaries];

    // Apply Reporter filter first (Reporters only see their assigned probates)
    if (this.isReporterOnly()) {
      filteredData = filteredData.filter(
        p => p.assignedUser?.id === this.loggedInUser2?.id
      );
      // Reporters fetch all statuses from server — apply status filter client-side
      if (this.selectedStatusFilters.length > 0) {
        filteredData = filteredData.filter(p => this.selectedStatusFilters.includes(p.status));
      }
    }

    // Apply user filter (for Admins/Supervisors)
    if (this.selectedUserFilter) {
      if (this.selectedUserFilter === 'unassigned') {
        filteredData = filteredData.filter(p => !p.assignedUser);
      } else {
        filteredData = filteredData.filter(
          p => p.assignedUser?.id === (this.selectedUserFilter as User).id
        );
      }
    }

    // Status filter is applied server-side for Admins/Supervisors via getProbates()

    // Reloading the dataset means any previously selected rows now refer to stale
    // object instances that aren't in the visible table — clear the selection so
    // they can't sneak into the next assign call.
    this.selection.clear();
    this.dataSource.data = filteredData;
  }

  goToReport(element: ProbateSummary) {
    this.router.navigate(['/reporting', element.probateId], {
      state: { user: this.loggedInUser2 }
    });
  }

  // Assign selected rows
  assignSelectedProbates() {
    const assignedUser = this.assignToUserControl.value;
    if (!assignedUser || this.selection.isEmpty()) return;

    if (assignedUser === 'unassign') {

      //JP this is an unassign activity
      this.selection.selected.forEach(probate => {
        probate.assignedUser = null;
      });

      this.unassignProbates(this.selection.selected);

      this.toastr.success(`Unassigned ${this.selection.selected.length} case(s)`, '', { timeOut: 3000 });

    } else {

      this.selection.selected.forEach(probate => {
        probate.assignedUser = assignedUser;
      });

      this.saveProbates(this.selection.selected);

      this.toastr.success(`Assigned ${this.selection.selected.length} case(s) to ${assignedUser.firstName} ${assignedUser.lastName}`, '', { timeOut: 3000 });

    }

    this.selection.clear();
    this.dataSource._updateChangeSubscription();
  }

  // Checkbox helpers — operate on the rows currently rendered (filter → sort → paginate)
  // so "select all" always matches what the user can actually see. Individual selections
  // made on other pages are preserved.
  private getPageRows(): ProbateSummary[] {
    return this.renderedRows;
  }

  isAllSelected() {
    const pageRows = this.getPageRows();
    if (pageRows.length === 0) return false;
    return pageRows.every(row => this.selection.isSelected(row));
  }

  isSomeSelectedOnPage(): boolean {
    const pageRows = this.getPageRows();
    const anySelected = pageRows.some(row => this.selection.isSelected(row));
    return anySelected && !this.isAllSelected();
  }

  masterToggle() {
    const pageRows = this.getPageRows();
    if (this.isAllSelected()) {
      // Deselect just the visible rows. Off-page hand-picks (if any) are left alone so
      // an accidental double-click doesn't wipe more than the user can see.
      pageRows.forEach(row => this.selection.deselect(row));
    } else {
      // "Select all" means exactly these visible rows — replace any prior selection
      // (from an earlier page or a different sort) so the assign call uses only
      // what the user is currently looking at.
      this.selection.clear();
      pageRows.forEach(row => this.selection.select(row));
    }
  }

  extractRouteValues() {
    // Get user from the UserService (shared across components)
    this.loggedInUser2 = this.userService.loggedInUser;
    this.loggedInUserTag = `${this.loggedInUser2?.lastName}, ${this.loggedInUser2?.firstName}`;
  }

  populateComponents() {
    forkJoin([this.getProbates(), this.getUsers()]).subscribe(results => {
      this.ProbateSummaries = results[0];
      this.users = results[1].sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // Resolve a restored user-filter id (from storage) against the freshly loaded users
      if (this.pendingUserFilterId !== undefined) {
        this.selectedUserFilter = this.pendingUserFilterId === 'unassigned'
          ? 'unassigned'
          : (this.users.find(u => u.id === this.pendingUserFilterId) ?? null);
        this.pendingUserFilterId = undefined;
      }

      // Pre-apply user filter for Supervisors, only on first load. hasInitialisedUserFilter is
      // also forced true by restoreFiltersFromStorage() whenever a previously saved filter set
      // exists (i.e. for any returning user), specifically so THIS auto-select-self default
      // doesn't clobber a restored filter - it is not a "has this component initialised yet"
      // flag in general, so nothing else should be gated on it (see canViewReportedAndVerifiedColumns
      // below, which used to be nested in here and, as a result, silently never ran for anyone
      // with saved filters, regardless of role).
      if (!this.hasInitialisedUserFilter) {
        this.hasInitialisedUserFilter = true;
        const isSupervisor = this.loggedInUser2?.roles?.some(r => r.id === 2 || r.code === 'Supervisor');
        if (isSupervisor) {
          const self = this.users.find(u => u.id === this.loggedInUser2?.id);
          if (self) this.selectedUserFilter = self;
        }
      }

      // Reported By / Verified By columns - role-gated visibility only, independent of the
      // one-shot user-filter flag above. The !includes() checks make this idempotent, so it's
      // safe to re-evaluate on every populateComponents() call (e.g. "Apply Filter").
      if (this.canViewReportedAndVerifiedColumns() && !this.displayedColumns.includes('reportedBy')) {
        const statusIndex = this.displayedColumns.indexOf('status');
        this.displayedColumns.splice(statusIndex, 0, 'reportedBy');
      }
      if (this.canViewReportedAndVerifiedColumns() && !this.displayedColumns.includes('verifiedBy')) {
        const statusIndex = this.displayedColumns.indexOf('status');
        this.displayedColumns.splice(statusIndex, 0, 'verifiedBy');
      }

      // activeView is the single source of truth for which of these four views should be
      // showing - set explicitly by whichever action (applyFilter/applyNameSearch/
      // applyProbateSearch/applyDateSearch) the user took most recently, and persisted as
      // that one value. The "&&" guards below are just a safety net for a restored view whose
      // underlying value somehow went missing (e.g. storage edited by hand) - in that case
      // fall back to the filter view rather than firing a request with nothing to search for.
      if (this.activeView === 'probateSearch' && this.probateSearchValue.trim()) {
        this.applyProbateSearch();
      } else if (this.activeView === 'nameSearch' && this.nameSearchValue.trim()) {
        this.applyNameSearch();
      } else if (this.activeView === 'dateSearch' && (this.startDate || this.endDate)) {
        this.applyDateSearch();
      } else {
        // Apply all filters (including Reporter-only filter)
        this.activeView = 'filter';
        this.applyAllFilters();
      }
    });
  }

  async saveProbates(probates: ProbateSummary[]) {
    const assignedUser = this.assignToUserControl.value as User;

    const parameters = new HttpParams()
      .set('useridtoassignto', assignedUser.id)
      .set('user', this.loggedInUserTag);

    const probateIds = probates.map(p => p.probateId);

    const response = this.http.post(
      "/charitycentral/saveprobateuserassignments",
      probateIds,
      { params: parameters }
    );

    return await lastValueFrom(response);
  }

  async unassignProbates(probates: ProbateSummary[]) {

    var stringProbates = JSON.stringify(probates);

    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag);

    var probateIds = probates.map(function (a) { return a.probateId });

    var response = this.http.post("/charitycentral/unassignprobates", probateIds, { params: parameters });

    return await lastValueFrom(response);

  }

  async getProbates() {
    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('includeNonCharitable', this.showNonCharitable.toString());

    // Reporters fetch all statuses so they can see assigned probates regardless of workflow status.
    // Admins/Supervisors use the selected status filter chips.
    const statusesToFetch = this.isReporterOnly()
      ? this.statusOptions.map(s => s.value)
      : this.selectedStatusFilters;

    for (const status of statusesToFetch) {
      parameters = parameters.append('statusesToGet', status.toString());
    }

    const response = this.http.get<ProbateSummary[]>("/charitycentral/getprobatesummaries", { params: parameters });
    return await lastValueFrom(response);
  }

  onShowNonCharitableChange() {
    this.populateComponents();
  }

  async getUsers() {
    const parameters = new HttpParams().set('user', this.loggedInUserTag);
    const result = this.http.get<User[]>('/charitycentral/getallusers', { params: parameters });
    return await lastValueFrom(result);
  }

  canAssignProbates(): boolean {
    if (!this.loggedInUser2 || !this.loggedInUser2.roles) {
      return false;
    }
    // Only Supervisor is a senior probate-processing role - SystemAdministrator is a separate
    // concern (Pricing/Admin menu access) and must not grant probate assignment/oversight.
    return this.loggedInUser2.roles.some(role =>
      role.id === 2 || role.code === 'Supervisor'
    );
  }

  // Gates the Reported By / Verified By columns - a pure visibility concern, deliberately
  // broader than canAssignProbates() (which gates actually assigning/overseeing probates and
  // must stay Supervisor-only). System Administrators need to see who reported/verified a
  // probate without being granted assignment/oversight actions.
  canViewReportedAndVerifiedColumns(): boolean {
    if (!this.loggedInUser2 || !this.loggedInUser2.roles) {
      return false;
    }
    return this.loggedInUser2.roles.some(role =>
      role.id === 1 || role.code === 'SystemAdministrator' || role.id === 2 || role.code === 'Supervisor'
    );
  }

  getStatusLabel(status: number): string {
    return this.statusOptions.find(s => s.value === status)?.label ?? 'Unknown';
  }

  getWeekOld(dateOfIssue: Date | string | null | undefined): number | string {
    if (!dateOfIssue) return '';
    const issueDate = new Date(dateOfIssue);
    if (isNaN(issueDate.getTime())) return '';
    return Math.floor((Date.now() - issueDate.getTime()) / 604800000);
  }

  compareUsers(u1: User | 'unassigned' | null, u2: User | 'unassigned' | null): boolean {
    if (u1 === u2) return true;
    if (typeof u1 === 'object' && u1 !== null && typeof u2 === 'object' && u2 !== null) {
      return u1.id === u2.id;
    }
    return false;
  }

  isReporterOnly(): boolean {
    if (!this.loggedInUser2?.roles) return false;
    // Reporter only if they have Reporter role but NOT SystemAdmin or Supervisor
    const hasReporter = this.loggedInUser2.roles.some(r => r.id === 4 || r.code === 'Reporter');
    const hasAdminOrSupervisor = this.canAssignProbates();
    return hasReporter && !hasAdminOrSupervisor;
  }
}
